import { TextFieldModule } from '@angular/cdk/text-field';
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChild,
    type ElementRef,
    input,
    model,
    output,
    type TemplateRef,
    viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NgIcon } from '@ng-icons/core';
import { QpxButton, QpxFormFieldDirectives, QpxIcon } from '@quipex/shared/ui';
import { QuippyBuildingSelectorSlot } from '../building-selector-slot';

export type QuippyQuestionBoxAppearance = 'filled' | 'elevated' | 'outlined';
export type QuippyScopeRowPosition = 'hidden' | 'above' | 'below';

let nextQuestionBoxId = 0;

@Component({
    selector: 'qpx-quippy-question-box',
    imports: [
        TextFieldModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        NgIcon,
        NgTemplateOutlet,
        QpxButton,
        QpxFormFieldDirectives,
        QpxIcon,
    ],
    templateUrl: './question-box.html',
    styleUrl: './question-box.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuippyQuestionBox {
    //#region Constants
    protected readonly inputId = `quippy-input-${nextQuestionBoxId++}`;
    //#endregion

    //#region Inputs
    public readonly appearance = input<QuippyQuestionBoxAppearance>('filled');
    public readonly ariaDescribedBy = input<string>();
    public readonly formAriaLabel = input('Ask Quippy a question');
    public readonly maxRows = input(5);
    public readonly compact = input(false);
    public readonly placeholder = input('Ask a question about your buildings…');
    public readonly question = model('');
    public readonly scopeRowDismissed = input(false);
    public readonly scopeRowPosition = input<QuippyScopeRowPosition>('below');
    public readonly scopeTemplate = input<TemplateRef<unknown>>();
    public readonly sendAriaLabel = input('Send question');
    public readonly sendDisabled = input(false);
    //#endregion

    //#region Outputs
    public readonly questionSubmitted = output<string>();
    //#endregion

    //#region Template references
    private readonly projectedScope = contentChild(QuippyBuildingSelectorSlot);
    protected readonly renderedScopeTemplate = computed(
        () => this.scopeTemplate() ?? this.projectedScope()?.templateRef
    );
    private readonly questionInput =
        viewChild.required<ElementRef<HTMLTextAreaElement>>('questionInput');
    //#endregion

    //#region Template methods
    protected onInput(): void {
        this.question.set(this.questionInput().nativeElement.value);
    }

    protected onKeyDown(event: KeyboardEvent): void {
        if (event.isComposing || event.key !== 'Enter' || event.shiftKey)
            return;

        event.preventDefault();
        this.submitQuestion();
    }

    protected onSubmit(event: SubmitEvent): void {
        event.preventDefault();
        this.submitQuestion();
    }

    public focusPrompt(): void {
        queueMicrotask(() => this.questionInput().nativeElement.focus());
    }
    //#endregion

    //#region Private methods
    private submitQuestion(): void {
        const question = this.question().trim();
        if (!question || this.sendDisabled()) return;

        this.questionSubmitted.emit(question);
    }
    //#endregion
}
